import React from 'react';
import {useQueryClient} from '@tanstack/react-query';

import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {Experiment} from 'iron/apps/experiments/src/types';
import {experimentQueryKeyForList} from 'iron/apps/experiments/src/react-query';

import {ListPageTrackingEvent, trackExperimentListPageAction} from '../tracking';
import {useExperimentPageContext} from '../context';
import {useQueryExperimentList} from './query-experiment-list';

export function useExperimentList() {
  const {projectId, workspaceId} = useExperimentPageContext();
  const queryClient = useQueryClient();

  const {experiments, experimentsLimit, isLoading, isError, error, isSuccess} = useQueryExperimentList({
    projectId,
    workspaceId,
  });

  React.useEffect(() => {
    if (isError && error) {
      addToast({
        message: error.message,
        type: ToastType.ERROR,
      });
    }
  }, [isError, error]);

  React.useEffect(() => {
    if (isSuccess) {
      trackExperimentListPageAction({
        event: ListPageTrackingEvent.ListLoaded,
        props: {
          'experiment count': experiments?.length ?? 0,
          'experiment limit': experimentsLimit,
        },
      });
    }
  }, [isSuccess, experiments, experimentsLimit]);

  const setExperiments = React.useCallback(
    (newExperiments: Experiment[]) => {
      queryClient.setQueriesData(
        {queryKey: experimentQueryKeyForList(projectId, workspaceId)},
        {
          results: newExperiments,
          limit: experimentsLimit,
        },
      );
    },
    [projectId, workspaceId, experimentsLimit, queryClient],
  );

  return {
    error,
    experiments,
    experimentsLimit,
    isError,
    isLoading,
    isSuccess,
    setExperiments,
  };
}
